import React, { useEffect, useState } from 'react';
import { X, Bell, Heart, MessageCircle, Users, UserPlus, Share2, Info } from 'lucide-react';
import { Avatar } from './Avatar';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { NotificationType } from '../services/notificationService';

interface NotificationData {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  avatarUrl?: string;
  userName?: string;
  userId?: number;
}

interface NotificationPopupProps {
  notification: NotificationData;
  onClose: () => void;
  onClick?: () => void;
  autoCloseDelay?: number;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case NotificationType.LIKE:
      return <Heart className="w-5 h-5 text-red-500" />;
    case NotificationType.COMMENT:
    case NotificationType.REPLY:
      return <MessageCircle className="w-5 h-5 text-blue-500" />;
    case NotificationType.FOLLOW:
    case NotificationType.FRIEND_REQUEST:
    case NotificationType.FRIEND_ACCEPT:
      return <UserPlus className="w-5 h-5 text-green-500" />;
    case NotificationType.SHARE:
      return <Share2 className="w-5 h-5 text-indigo-500" />;
    case NotificationType.GROUP_INVITE:
    case NotificationType.GROUP_JOIN:
      return <Users className="w-5 h-5 text-purple-500" />;
    case NotificationType.SYSTEM:
    case NotificationType.POST_MODERATION:
      return <Info className="w-5 h-5 text-amber-500" />;
    default:
      return <Bell className="w-5 h-5 text-gray-500" />;
  }
};

const getNotificationColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.LIKE:
      return 'from-red-50 to-pink-50 border-red-200';
    case NotificationType.COMMENT:
    case NotificationType.REPLY:
      return 'from-blue-50 to-cyan-50 border-blue-200';
    case NotificationType.FOLLOW:
    case NotificationType.FRIEND_REQUEST:
    case NotificationType.FRIEND_ACCEPT:
      return 'from-green-50 to-emerald-50 border-green-200';
    case NotificationType.SHARE:
      return 'from-indigo-50 to-purple-50 border-indigo-200';
    case NotificationType.GROUP_INVITE:
    case NotificationType.GROUP_JOIN:
      return 'from-purple-50 to-fuchsia-50 border-purple-200';
    case NotificationType.SYSTEM:
    case NotificationType.POST_MODERATION:
      return 'from-amber-50 to-yellow-50 border-amber-200';
    default:
      return 'from-gray-50 to-slate-50 border-gray-200';
  }
};

export const NotificationPopup: React.FC<NotificationPopupProps> = ({
  notification,
  onClose,
  onClick,
  autoCloseDelay = 5000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Slide in animation
    setTimeout(() => setIsVisible(true), 100);

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - (100 / (autoCloseDelay / 100));
        return newProgress > 0 ? newProgress : 0;
      });
    }, 100);

    // Auto close after delay
    const timer = setTimeout(() => {
      handleClose();
    }, autoCloseDelay);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [autoCloseDelay]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    handleClose();
  };

  return (
    <div
      className={`fixed bottom-6 left-6 z-[9999] transition-all duration-300 transform ${
        isVisible && !isClosing
          ? 'translate-y-0 opacity-100 scale-100'
          : 'translate-y-4 opacity-0 scale-95'
      }`}
    >
      <div
        onClick={handleClick}
        className={`bg-gradient-to-br ${getNotificationColor(notification.type)} rounded-xl shadow-2xl border backdrop-blur-sm p-4 w-96 cursor-pointer hover:shadow-3xl transition-all hover:scale-[1.02] relative overflow-hidden`}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200/30">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex items-start space-x-3 mt-1">
          {/* Icon/Avatar */}
          <div className="flex-shrink-0">
            {notification.avatarUrl || notification.userName ? (
              <div className="relative">
                <Avatar
                  user={{
                    avatarUrl: notification.avatarUrl,
                    displayName: notification.userName || 'User',
                    userId: notification.userId || 0
                  }}
                  size="md"
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                  {getNotificationIcon(notification.type)}
                </div>
              </div>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                {getNotificationIcon(notification.type)}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-sm text-gray-900 truncate">
                {notification.title}
              </h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="text-gray-400 hover:text-gray-700 transition-colors ml-2 hover:bg-white/60 rounded-lg p-1"
                aria-label="Đóng"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-sm text-gray-700 line-clamp-2 mb-2 leading-relaxed">
              {notification.message}
            </p>
            
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {(() => {
                try {
                  const date = new Date(notification.timestamp);
                  return isNaN(date.getTime()) 
                    ? 'Vừa xong'
                    : formatDistanceToNow(date, { addSuffix: true, locale: vi });
                } catch {
                  return 'Vừa xong';
                }
              })()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
